import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import GoToLink from '@/components/GoToLink';
import { Modal } from '@/components/Modal';
import PulsingLoader, { LoaderHolder } from '@/components/PulsingLoader';
import ScrollableTable from '@/components/SimpleScrollableTable';
import { Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import { api } from '@/utils/api';

const Dialog = styled(StyledDialog)`
  width: 464px;
  text-align: left;
  padding: 0;
  width: 1000px;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
`;

const Inner = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const Footer = styled.div`
  padding: 24px;
  display: flex;
  justify-content: flex-end;
`;

const StyledTable = styled(Table)`
  box-shadow: 0px -1px 0px 0px #e9e9e9 inset;

  th,
  td {
    min-width: 100px;
    &:first-child {
      padding-left: 24px;
      min-width: 240px;
    }
    &:nth-child(2),
    &:nth-child(3) {
      min-width: 160px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }

  td {
    /* font-size: 12px; */
    font-weight: 700;
  }
`;

interface EYTVLinkVideoStats {
  eytv_link_id: number;
  title: string;
  video_duration: number;
  prepared_video_id: string;
  external_id: string;
  ctr_percentage?: number;
  quartile_p25_rate?: number;
  quartile_p50_rate?: number;
  quartile_p75_rate?: number;
  quartile_p100_rate?: number;
  video_view_rate_percentage?: number;
  video_views?: number;
  eytv_1m?: number;
  eytv_2m?: number;
  eytv_4m?: number;
  eytv_8m?: number;
  eytv_30m?: number;
  e_1m_views_percentage?: number;
  e_2m_views_percentage?: number;
  e_4m_views_percentage?: number;
  e_8m_views_percentage?: number;
  e_30m_views_percentage?: number;
}

interface Props {
  onClose: VoidFunction;
  fileId: number;
}

const ResultsModal: FC<Props> = ({ fileId, onClose }) => {
  const { data, isLoading, isError } = useQuery(
    ['eytv_file_stats', { fileId }],
    () =>
      api.get<never, EYTVLinkVideoStats[]>(`/v1/eytv-files/${fileId}/stats`),
    {
      staleTime: 120_000,
    },
  );

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">eYTV results</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Inner>
          {isLoading ? (
            <LoaderHolder>
              <PulsingLoader />
              <Text $color="gray" $type="captionSemibold">
                Loading...
              </Text>
            </LoaderHolder>
          ) : (
            <ScrollableTable>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Video Title</Th>
                    <Th>
                      Youtube Link
                      <br />
                      <Text as="span" $color="gray" $type="extraSmall">
                        Original
                      </Text>
                    </Th>
                    <Th>
                      Youtube Link
                      <br />
                      <Text as="span" $color="gray" $type="extraSmall">
                        Trimmed
                      </Text>
                    </Th>
                    <Th>Video Duration</Th>
                    <Th>Views</Th>
                    <Th>View Rate %</Th>
                    <Th>p25</Th>
                    <Th>p50</Th>
                    <Th>p75</Th>
                    <Th>p100</Th>
                    <Th>eYTV 1min</Th>
                    <Th>eYTV 2min</Th>
                    <Th>eYTV 4min</Th>
                    <Th>eYTV 8min</Th>
                    <Th>eYTV 30min</Th>
                    <Th>e1min View Rate %</Th>
                    <Th>e2min View Rate %</Th>
                    <Th>e4min View Rate %</Th>
                    <Th>e8min View Rate %</Th>
                    <Th>e30min View Rate %</Th>
                  </tr>
                </thead>
                <tbody>
                  <TableContent
                    isLoading={isLoading}
                    isError={isError}
                    hasData={!!data?.length}
                  >
                    {data?.map((row) => (
                      <tr key={row.eytv_link_id}>
                        <Td>{row.title}</Td>
                        <Td>
                          <GoToLink
                            title="Go to Youtube"
                            link={`https://youtube.com/watch?v=${row.external_id}`}
                          />
                        </Td>
                        <Td>
                          <GoToLink
                            title="Go to Youtube"
                            link={`https://youtube.com/watch?v=${row.prepared_video_id}`}
                          />
                        </Td>
                        <Td>
                          {Duration.fromObject({
                            seconds: row.video_duration,
                          }).toFormat('mm:ss')}
                        </Td>
                        <Td>{row.video_views ?? '-'}</Td>
                        <Td>{row.video_view_rate_percentage ?? '-'}</Td>
                        <Td>{row.quartile_p25_rate ?? '-'}</Td>
                        <Td>{row.quartile_p50_rate ?? '-'}</Td>
                        <Td>{row.quartile_p75_rate ?? '-'}</Td>
                        <Td>{row.quartile_p100_rate ?? '-'}</Td>
                        <Td>{row.eytv_1m ?? '-'}</Td>
                        <Td>{row.eytv_2m ?? '-'}</Td>
                        <Td>{row.eytv_4m ?? '-'}</Td>
                        <Td>{row.eytv_8m ?? '-'}</Td>
                        <Td>{row.eytv_30m ?? '-'}</Td>
                        <Td>{row.e_1m_views_percentage ?? '-'}</Td>
                        <Td>{row.e_2m_views_percentage ?? '-'}</Td>
                        <Td>{row.e_4m_views_percentage ?? '-'}</Td>
                        <Td>{row.e_8m_views_percentage ?? '-'}</Td>
                        <Td>{row.e_30m_views_percentage ?? '-'}</Td>
                      </tr>
                    ))}
                  </TableContent>
                </tbody>
              </StyledTable>
            </ScrollableTable>
          )}
        </Inner>
        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ResultsModal;
